import { featureFlagClient } from '@trello/feature-flag-client';
import { Analytics } from '@trello/atlassian-analytics';
import { sendErrorEvent } from '@trello/error-reporting';
import { MessagesFromServiceWorker } from '@trello/service-worker';
import { sendStorageError } from 'app/scripts/init/handleStorageError';

const handleServiceWorkerMessage = (event: MessagesFromServiceWorker) => {
  console.log('received event from service worker', event);
  switch (event.type) {
    case 'QUOTA_EXCEEDED': {
      sendStorageError({ isFromServiceWorker: true });
      break;
    }
    default: {
      console.warn('Unrecognized message from service worker', event);
    }
  }
};

const unregisterWorkers = () => {
  // Unsubscribe to messages from the worker
  navigator.serviceWorker.onmessage = () => {};

  // On specific browsers (iOS) getRegistrations() can potentially return undefined
  // so be paranoid about checking for that before unpacking the response
  navigator.serviceWorker.getRegistrations()?.then((registrations) => {
    if (registrations.length > 0) {
      console.log(`[SERVICE WORKER] Unregistering`);
      for (let i = 0; i < registrations.length; i++) {
        Analytics.sendOperationalEvent({
          actionSubject: 'serviceWorker',
          action: 'unregistered',
          source: 'appStartup',
        });
        registrations[i].unregister();
      }
    }
  });
};

const registerWorker = () => {
  // subscribe to messages from the worker
  navigator.serviceWorker.onmessage = (event) =>
    handleServiceWorkerMessage(event.data);

  // On specific browsers (iOS) register() can potentially return undefined
  // so be paranoid about checking for that before unpacking the response
  navigator.serviceWorker
    .register('/service-worker.js')
    ?.then(() => {
      console.log('[SERVICE WORKER] Registered');
      Analytics.sendOperationalEvent({
        actionSubject: 'serviceWorker',
        action: 'registered',
        source: 'appStartup',
      });
    })
    .catch((registrationError: Error) => {
      console.log('[SERVICE WORKER] Registration failed');
      if (
        registrationError.message.indexOf(
          'ServiceWorker script evaluation failed',
        ) > -1
      ) {
        // Older browsers like Chrome 49 and Chrome 51 fail to register the worker
        // because of some kind of syntax error in service-worker.js. It is
        // extremely difficult to debug this problem in the absence of a working
        // copy of these versions, they are unsupported versions, and the worker
        // is solely a performance optimization, so we're just silencing the error
        // in this scenario and moving on.
      } else {
        sendErrorEvent(registrationError, {
          tags: { ownershipArea: 'trello-fe-platform' },
        });
      }
      unregisterWorkers();
    });
};

export const registerOrUnregisterServiceWorker = () => {
  // Check that both 'navigator' and 'navigator.serviceWorker' are present
  // and not undefined or null
  if (navigator && navigator.serviceWorker) {
    const isServiceWorkerEnabled = featureFlagClient.get(
      'fep.service_worker',
      false,
    );

    featureFlagClient.on('fep.service_worker', false, (newValue) => {
      if (newValue === false) {
        unregisterWorkers();
      } else {
        registerWorker();
      }
    });

    if (isServiceWorkerEnabled) {
      registerWorker();
    } else {
      unregisterWorkers();
    }
  }
};
